import { useCallback, useEffect, useRef } from 'react';
import { ACWResult } from '../components/acw/acw.types';
import { AgentState, CallState, UnavailableCode } from '../types';
import { logger } from '../utils/logger';
import { loadAcwPendingContext } from './agent-widget/acw-pending-context';
import { getAcwLockedContactId } from './agent-widget/acw-ui-lock';
import { useAcwState } from './agent-widget/useAcwState';
import { useCallTracking } from './agent-widget/useCallTracking';
import { useOutboundDial } from './agent-widget/useOutboundDial';
import { useSapCallLinking } from './agent-widget/useSapCallLinking';
import { completeAcwSession } from './agent-widget/completeAcw';

export type { LastCallInfo, OutboundCallInfo } from './agent-widget/types';

function resolveAcwContactId(
  lastCallInfo: ReturnType<typeof useCallTracking>['lastCallInfo'],
  callState: CallState
): string | undefined {
  return (
    lastCallInfo?.contactId ||
    getAcwLockedContactId() ||
    callState.contactId ||
    loadAcwPendingContext()?.contactId ||
    undefined
  );
}

interface UseAgentWidgetIntegrationParams {
  callState: CallState;
  isRinging: boolean;
  agentState: AgentState;
  unavailableCodes: UnavailableCode[];
  isInAcw: boolean;
  setAgentState: (state: string, reasonCode?: string) => Promise<void>;
}

export function useAgentWidgetIntegration({
  callState,
  isRinging,
  agentState,
  unavailableCodes,
  isInAcw,
  setAgentState,
}: UseAgentWidgetIntegrationParams) {
  const { lastCallInfo, lastCallerPhone, setLastCallerPhone, clearCallTracking } = useCallTracking(
    callState,
    isInAcw
  );

  const { outboundCallInfo, isOutboundCall, resetOutboundState } = useOutboundDial({
    setLastCallerPhone,
  });

  const {
    sapContactState,
    isSapC4cEnabled,
    manualSearchState,
    openedSapContacts,
    handleSapContactOpened,
    handleManualSearch,
    resetSapLinking,
  } = useSapCallLinking({
    callState,
    isRinging,
    preferredContactId: outboundCallInfo?.sapContactId ?? null,
    preferredReferenceId: outboundCallInfo?.referenceId ?? null,
  });

  const {
    showACW,
    conferenceState,
    handleCallEnd,
    handleTransferComplete,
    handleConferenceInitiate,
    dismissAcw,
  } = useAcwState({
    callState,
    isRinging,
    agentState,
    unavailableCodes,
    isInAcw,
  });

  const acwSubmissionRef = useRef({ inFlight: false, completed: false });
  const prevShowAcwRef = useRef(false);
  const outboundProgressSeenRef = useRef(false);

  useEffect(() => {
    const agentStateName = agentState.state?.toLowerCase() ?? '';
    if (
      callState.status === 'connecting' ||
      callState.status === 'active' ||
      callState.status === 'holding' ||
      agentStateName === 'outboundcontact'
    ) {
      outboundProgressSeenRef.current = true;
    }
  }, [callState.status, agentState.state]);

  useEffect(() => {
    if (!isOutboundCall || showACW || isRinging || callState.status !== 'idle') {
      return;
    }

    if (!outboundProgressSeenRef.current) {
      return;
    }

    const agentStateName = agentState.state?.toLowerCase() ?? '';
    if (agentStateName !== 'available') {
      return;
    }

    logger.lifecycle('[Widget] Outbound attempt finished — clearing outbound UI state');
    outboundProgressSeenRef.current = false;
    resetOutboundState();
    resetSapLinking();
  }, [
    isOutboundCall,
    showACW,
    isRinging,
    callState.status,
    agentState.state,
    resetOutboundState,
    resetSapLinking,
  ]);

  useEffect(() => {
    if (showACW && !prevShowAcwRef.current) {
      acwSubmissionRef.current = { inFlight: false, completed: false };
    }
    prevShowAcwRef.current = showACW;
  }, [showACW]);

  const acwCompleteContextRef = useRef({
    lastCallInfo,
    lastCallerPhone,
    openedSapContacts,
    sapContactState,
    manualSearchState,
    isOutboundCall,
    outboundCallInfo,
    callState,
    requireDisposition: false,
  });
  acwCompleteContextRef.current = {
    lastCallInfo,
    lastCallerPhone,
    openedSapContacts,
    sapContactState,
    manualSearchState,
    isOutboundCall,
    outboundCallInfo,
    callState,
    requireDisposition:
      lastCallInfo?.requireDisposition ||
      callState.requireDisposition ||
      loadAcwPendingContext()?.requireDisposition ||
      false,
  };

  const handleACWComplete = useCallback(
    async (result: ACWResult) => {
      if (acwSubmissionRef.current.inFlight || acwSubmissionRef.current.completed) {
        logger.lifecycle('[ACW Flow] ACW completion ignored — duplicate submission');
        return;
      }

      acwSubmissionRef.current.inFlight = true;
      logger.lifecycle('[ACW Flow] ACW completion started', {
        dispositionId: result.dispositionId,
        requireDisposition: acwCompleteContextRef.current.requireDisposition,
        isOutboundCall: acwCompleteContextRef.current.isOutboundCall,
      });

      try {
        const ctx = acwCompleteContextRef.current;
        const acwContactId = resolveAcwContactId(ctx.lastCallInfo, ctx.callState);
        const displayedContacts = [
          ...ctx.sapContactState.contacts,
          ...ctx.manualSearchState.contacts.filter(
            (manualContact) =>
              !ctx.sapContactState.contacts.some((contact) => contact.id === manualContact.id)
          ),
        ];

        await completeAcwSession({
          result,
          lastCallInfo: ctx.lastCallInfo,
          acwContactId,
          requireDisposition: ctx.requireDisposition,
          lastCallerPhone: ctx.lastCallerPhone,
          openedSapContacts: ctx.openedSapContacts,
          displayedContacts,
          isOutboundCall: ctx.isOutboundCall,
          outboundCallInfo: ctx.outboundCallInfo,
        });

        await setAgentState('Available');
        dismissAcw();
        acwSubmissionRef.current.completed = true;
        clearCallTracking();
        resetOutboundState();
        resetSapLinking();
        logger.lifecycle('[ACW Flow] ACW completion finished — agent set Available');
      } catch (error) {
        acwSubmissionRef.current.completed = false;
        logger.error('[ACW Flow] ACW completion failed:', error);
        throw error;
      } finally {
        acwSubmissionRef.current.inFlight = false;
      }
    },
    [setAgentState, dismissAcw, clearCallTracking, resetOutboundState, resetSapLinking]
  );

  return {
    sapContactState,
    isSapC4cEnabled,
    manualSearchState,
    openedSapContacts,
    showACW,
    lastCallInfo,
    lastCallerPhone,
    conferenceState,
    isOutboundCall,
    outboundCallInfo,
    handleACWComplete,
    handleCallEnd,
    handleTransferComplete,
    handleConferenceInitiate,
    handleSapContactOpened,
    handleManualSearch,
  };
}
